// Reading KataGo's stored analyses of a finished game (SPEC §6.6, §9, §11.3): the points each move
// lost, the mistakes, and the suggested next moves. Everything is computed from the loaded game;
// nothing is stored.
import { Stone } from './Board';

// A move losing at least this many points is a mistake (owner, 2026-10-15).
export const MISTAKE_POINTS = 5;
// Suggestions other than the best are shown when they lose at most this many points against it.
export const SUGGESTION_RANGE = 2;

const isStone = m => m && m.move && !m.move.startsWith('.');

// KataGo's analysis stored on record p, or null.
export function analysisAt(game, p) {
  const m = game.moves[p];
  const a = m && m.analysis;
  return a && a.engine ? a : null;
}

// The last position: the record of the last stone (a resignation or pass adds no analysed position).
export function lastPosition(game) {
  let p = 0;
  for(const m of game.moves)
    if(m.index > p && isStone(m)) p = m.index;
  return p;
}

// +1 for black, −1 for white: turns black's lead into the lead of that colour.
const sign = stone => (stone === Stone.WHITE ? -1 : 1);

// The colour to move at position p.
function toMove(game, p) {
  const m = game.moves[p];
  if(!m || p === 0 || !m.stone || m.stone === Stone.NONE) return Stone.BLACK;
  return m.stone === Stone.BLACK ? Stone.WHITE : Stone.BLACK;
}

// How move p (a stone) fared: { lost, best } where lost is the points it lost (at least 0) and best
// tells whether it was KataGo's first choice; null when p or p − 1 has no analysis.
export function judge(game, p) {
  const m = game.moves[p];
  if(p < 1 || !isStone(m)) return null;
  const before = analysisAt(game, p - 1), after = analysisAt(game, p);
  if(!before || !after) return null;
  const lost = Math.max(0, sign(m.stone) * ((before.scoreLead || 0) - (after.scoreLead || 0)));
  const first = before.best && before.best[0] && before.best[0].move === m.move;
  return { lost, best: !!first };
}

// The moves that lost at least MISTAKE_POINTS: [{ index, stone }].
export function mistakes(game) {
  const list = [];
  for(const m of game.moves) {
    const j = judge(game, m.index);
    if(j && j.lost >= MISTAKE_POINTS) list.push({ index: m.index, stone: m.stone });
  }
  return list;
}

// KataGo's suggested next moves at position p, best first: [{ move, rank, lead, line }]. The best
// always, the others when they lose at most SUGGESTION_RANGE points against it; passes are left out.
// lead: black's lead after the move; line: the moves KataGo expects from it (null without one).
// pass tells whether the best next move is a pass.
export function suggestions(game, p) {
  const a = analysisAt(game, p);
  if(!a || !a.best || a.best.length === 0) return { moves: [], pass: false };
  const s = sign(toMove(game, p));
  const top = s * (a.best[0].scoreLead || 0);
  const moves = [];
  a.best.forEach((b, i) => {
    if(!b.move || b.move.toLowerCase() === 'pass') return;
    if(i === 0 || top - s * (b.scoreLead || 0) <= SUGGESTION_RANGE)
      moves.push({ move: b.move, rank: moves.length + 1, lead: b.scoreLead || 0,
                   line: b.line && b.line.length ? b.line : null });
  });
  return { moves, pass: a.best[0].move.toLowerCase() === 'pass' };
}

// Whether every position 0..last has an analysis.
export function complete(game) {
  const last = lastPosition(game);
  for(let p = 0; p <= last; p++) {
    const m = game.moves[p];
    if(m && (p === 0 || isStone(m)) && !analysisAt(game, p)) return false;
  }
  return true;
}

// Whether any position has an analysis.
export function any(game) {
  return game.moves.some(m => m.analysis && m.analysis.engine);
}

// ---- KataGo's ownership (SPEC §6.6, §11.3): who KataGo expects to own each point at the end.

// A point's owner counts as changed when its ownership moves by at least this much (−1 … 1).
export const MAP_CHANGE = 0.5;
// KataGo's estimate shows the points whose ownership is at least this far from 0.
export const ESTIMATE_SURE = 0.6;

// The stored ownership (one character per point, 'a' = −1 … 'u' = 1, row by row from the top
// left) as numbers, black positive; null without one.
export function decodeOwnership(s) {
  if(!s) return null;
  const v = new Array(s.length);
  for(let i = 0; i < s.length; i++) v[i] = (s.charCodeAt(i) - 97) / 10 - 1;
  return v;
}

// The estimate from KataGo's ownership at position p, in the engine's point-map form ("A19 1 B19
// -1 …": the points it is fairly sure of), or null when p has none.
export function ownershipPointMap(game, p) {
  const a = analysisAt(game, p);
  const own = decodeOwnership(a && a.ownership);
  if(!own || own.length !== game.grid * game.grid) return null;
  const parts = [];
  own.forEach((v, i) => {
    if(Math.abs(v) >= ESTIMATE_SURE) parts.push(`${game.encode(i % game.grid, Math.floor(i / game.grid))} ${v > 0 ? 1 : -1}`);
  });
  return parts.join(' ');
}

// The points whose owner changes from map `from` to map `to`: [{ x, y, colour }], colour
// colours[0] where black gains and colours[1] where white gains; none on the points `skip` ({x, y}).
export function changedPoints(grid, from, to, colours, skip = []) {
  if(!from || !to || from.length !== grid * grid || to.length !== grid * grid) return null;
  const list = [];
  for(let i = 0; i < to.length; i++) {
    const d = to[i] - from[i];
    if(Math.abs(d) < MAP_CHANGE) continue;
    const x = i % grid, y = Math.floor(i / grid);
    if(skip.some(s => s.x === x && s.y === y)) continue;
    list.push({ x, y, colour: d > 0 ? colours[0] : colours[1] });
  }
  return list;
}

// The 实战 / 推荐 / 对比 views of stone p (SPEC §6.6), or null when p is no mistake or KataGo's
// move before it has no line. played: the stone; best: KataGo's best move before it, with its line
// and the steps along it; actual: the move played followed by KataGo's expected answers, with the
// analysis after each step ({ scoreLead, ownership }); base: the ownership before the stone.
export function comparison(game, p) {
  const j = judge(game, p);
  if(!j || j.lost < MISTAKE_POINTS) return null;
  const before = analysisAt(game, p - 1), after = analysisAt(game, p);
  const best = before.best && before.best[0];
  if(!best || !best.line || best.line.length === 0) return null;
  const answer = after.best && after.best[0];
  const answers = answer && answer.line ? answer.line : [];
  return {
    p,
    lost: j.lost,
    played: game.moves[p],
    best: { move: best.move, lead: best.scoreLead || 0, line: best.line, steps: best.steps || [] },
    actual: {
      move: game.moves[p].move, lead: after.scoreLead || 0, line: [game.moves[p].move, ...answers],
      steps: [{ scoreLead: after.scoreLead || 0, ownership: after.ownership }, ...((answer && answer.steps) || [])],
    },
    base: before.ownership || null,
  };
}

// The mistakes before and after position p: { prev, next } (record indices, or null).
export function mistakesAround(game, p) {
  let prev = null, next = null;
  for(const m of mistakes(game)) {
    if(m.index < p) prev = m.index;
    else if(m.index > p && next === null) next = m.index;
  }
  return { prev, next };
}

// The part of the board a point is in: 'top-left', 'top', … 'bottom-right', 'centre' (thirds).
export function regionOf(x, y, grid) {
  const col = Math.min(2, Math.floor(x * 3 / grid)), row = Math.min(2, Math.floor(y * 3 / grid));
  return [['top-left', 'top', 'top-right'], ['left', 'centre', 'right'], ['bottom-left', 'bottom', 'bottom-right']][row][col];
}

// Labels for a view (SPEC §6.6), from the stones on the board before the stone (`board`: get(x, y))
// and the ownership before (`from`) and at the shown step (`to`): per colour, the most stones of one
// part of the board that KataGo expects to die (from theirs or unsure to the opponent's), and those
// it expects to live (from unsure or the opponent's to theirs), when at least 2. [{ kind: 'dies' |
// 'lives', stone: 'black' | 'white', region, n }], those that die first.
export function stoneLabels(board, grid, from, to) {
  if(!from || !to) return [];
  const groups = {};
  for(let y = 0; y < grid; y++) for(let x = 0; x < grid; x++) {
    const stone = board.get(x, y);
    if(stone !== Stone.BLACK && stone !== Stone.WHITE) continue;
    const s = sign(stone), i = y * grid + x;
    let kind = null;
    if(s * from[i] > -MAP_CHANGE && s * to[i] <= -MAP_CHANGE) kind = 'dies';
    else if(s * from[i] < MAP_CHANGE && s * to[i] >= MAP_CHANGE) kind = 'lives';
    if(!kind) continue;
    const key = `${kind} ${stone === Stone.BLACK ? 'black' : 'white'} ${regionOf(x, y, grid)}`;
    groups[key] = (groups[key] || 0) + 1;
  }
  const best = {};
  for(const [key, n] of Object.entries(groups)) {
    const [kind, stone, region] = key.split(' ');
    const k = `${kind} ${stone}`;
    if(n >= 2 && (!best[k] || n > best[k].n)) best[k] = { kind, stone, region, n };
  }
  return Object.values(best).sort((a, b) => (a.kind === b.kind ? b.n - a.n : (a.kind === 'dies' ? -1 : 1)));
}

// Whether KataGo's move answered near the opponent's last stone while the stone played went
// elsewhere ('local'), or the other way round ('tenuki'), or neither (null). Points {x, y}.
export function answerLabel(last, played, best) {
  if(!last) return null;
  const d = a => Math.max(Math.abs(a.x - last.x), Math.abs(a.y - last.y));
  if(d(best) <= 2 && d(played) >= 5) return 'local';
  if(d(played) <= 2 && d(best) >= 5) return 'tenuki';
  return null;
}

// ---- Practice (SPEC §6.7): guess a better move at the position before a mistake.

// A guess losing at most this much against KataGo's best move is a good answer (the same range in
// which the replay shows a move as a suggestion).
export const PRACTICE_GOOD = SUGGESTION_RANGE;
// A policy letter at least this far down the scale (below about 0.5 %; 'a' + floor(−4·log10(p)))
// means KataGo dismissed the point at a glance.
export const POLICY_DISMISSED = 'j';

// The mistakes to practise, in game order: those of `colour` (Stone.BLACK or Stone.WHITE) when it
// made any, otherwise both players'. [{ index, stone, own }], own: only the viewer's colour.
export function practiceMistakes(game, colour) {
  const all = mistakes(game);
  const own = all.filter(m => m.stone === colour);
  return own.length > 0 ? own.map(m => ({ ...m, own: true })) : all.map(m => ({ ...m, own: false }));
}

// The verdict on guessing `move` instead of stone p: { kind, points, better, worse, hint }.
// kind: 'same' (the stone played), 'best' (KataGo's first choice), 'good' (within PRACTICE_GOOD;
// points lost), 'not-yet' (better than the stone played by `better`, worse than the best by
// `worse`), 'worse' (lost `points` more than the stone played), or, for a move KataGo didn't search,
// 'dismissed' (its first glance gave it almost nothing) or 'unknown'; hint 'far' or 'near': whether
// KataGo's choice is at least 5 lines away from the guess.
export function practiceVerdict(game, p, move) {
  const m = game.moves[p];
  const a = analysisAt(game, p - 1);
  const j = judge(game, p);
  if(!m || !a || !j) return null;
  if(move === m.move) return { kind: 'same' };
  const s = sign(m.stone);
  const searched = (a.candidates && a.candidates.length ? a.candidates : a.best || [])
    .filter(c => c.move && c.move.toLowerCase() !== 'pass');
  const top = searched[0];
  if(!top) return { kind: 'unknown', hint: null };
  const c = searched.find(x => x.move === move);
  if(c) {
    const loss = Math.max(0, s * ((top.scoreLead || 0) - (c.scoreLead || 0)));
    if(c === top) return { kind: 'best' };
    if(loss <= PRACTICE_GOOD) return { kind: 'good', points: loss };
    if(loss < j.lost) return { kind: 'not-yet', better: j.lost - loss, worse: loss };
    return { kind: 'worse', points: loss - j.lost };
  }
  const g = game.decode(move), b = game.decode(top.move);
  const hint = Math.max(Math.abs(g.x - b.x), Math.abs(g.y - b.y)) >= 5 ? 'far' : 'near';
  const letter = a.policy && a.policy[g.y * game.grid + g.x];
  return { kind: letter && letter !== '.' && letter >= POLICY_DISMISSED ? 'dismissed' : 'unknown', hint };
}
